import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Target, Plus, Settings, Info, Activity, Brain, 
  Heart, TrendingUp, Star, Save, Calendar, Clock,
  ChevronRight, AlertCircle, X
} from 'lucide-react';
import { useHabitStore } from '../../store/habitStore';
import { categories } from '../../data/habitCategories';
import { HabitCard } from './HabitCard';
import { HabitMetrics } from './HabitMetrics';
import { HabitStreak } from './HabitStreak';
import { HabitExitDialog } from './HabitExitDialog';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface HabitTrackerProps {
  onSave?: () => void;
}

export function HabitTracker({ onSave }: HabitTrackerProps) {
  const { habits, toggleHabitCompletion, saveHabits } = useHabitStore();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const navigate = useNavigate();

  // Calculate overall stats
  const stats = {
    totalHabits: habits?.length || 0,
    activeStreak: Math.max(...(habits?.map(h => h.currentStreak) || [0])),
    completionRate: habits?.length ? Math.round(
      (habits.reduce((sum, h) => sum + h.completedDays, 0) / 
      habits.reduce((sum, h) => sum + h.targetDays, 0)) * 100
    ) : 0,
    bestPerforming: habits?.sort((a, b) => b.completedDays - a.completedDays)[0]?.name
  };

  const filteredHabits = selectedCategory === 'all' 
    ? habits
    : habits?.filter(h => h.category === selectedCategory) || [];

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await saveHabits();
      if (onSave) {
        onSave();
      }
      navigate('/home');
    } catch (error) {
      console.error('Error saving habits:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDismiss = () => {
    const hasUnsavedChanges = habits?.some(h => 
      new Date(h.lastUpdated) > new Date(h.lastSaved)
    );
    
    if (hasUnsavedChanges) {
      setShowExitDialog(true);
    } else {
      navigate('/home');
    }
  };

  if (!habits) {
    return (
      <div className="text-center py-8">
        <div className="text-muted-foreground">Loading habits...</div>
      </div>
    );
  }

  const completedToday = habits.filter(h => 
    h.lastCompleted === format(selectedDate, 'yyyy-MM-dd')
  ).length;

  return (
    <div className="space-y-6">
      {/* Date Selection */}
      <div className="theme-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary" />
            <div>
              <div className="font-medium">{format(selectedDate, 'EEEE, MMMM d')}</div>
              <div className="text-sm text-muted-foreground">
                {completedToday} of {habits.length} habits completed
              </div>
            </div>
          </div>
          <input
            type="date"
            value={format(selectedDate, 'yyyy-MM-dd')}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="bg-muted/50 px-3 py-1.5 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="theme-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Activity className="w-4 h-4" />
            <span>Streak</span>
          </div>
          <div className="text-2xl font-bold">{stats.activeStreak}</div>
          <div className="flex items-center gap-1 text-xs text-success mt-1">
            <TrendingUp className="w-3 h-3" />
            <span>Best ever: {Math.max(...habits.map(h => h.bestStreak))}</span>
          </div>
        </div>

        <div className="theme-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Target className="w-4 h-4" />
            <span>Success Rate</span>
          </div>
          <div className="text-2xl font-bold">{stats.completionRate}%</div>
          <div className="text-xs text-muted-foreground mt-1">Last 7 days</div>
        </div>

        <div className="theme-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Clock className="w-4 h-4" />
            <span>Next Due</span>
          </div>
          <div className="text-lg font-bold truncate">
            {format(new Date(), 'HH:mm')}
          </div>
          <div className="text-xs text-warning flex items-center gap-1 mt-1">
            <AlertCircle className="w-3 h-3" />
            <span>2 habits due soon</span>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 theme-scrollbar">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
            selectedCategory === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/70'
          }`}
        >
          All Habits
        </button>
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
              selectedCategory === category.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/70'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Active Habits */}
      <div className="theme-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Active Habits</h3>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
              <Settings className="w-4 h-4" />
              <span>Manage</span>
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {filteredHabits.filter(h => h.isActive).map((habit) => (
            <div key={habit.id} className="space-y-4">
              <HabitCard
                habit={habit}
                onComplete={(id) => toggleHabitCompletion(id, format(selectedDate, 'yyyy-MM-dd'))}
                onEdit={() => {}}
              />
              <HabitStreak history={habit.history} />
            </div>
          ))}

          {filteredHabits.filter(h => h.isActive).length === 0 && (
            <div className="text-center py-8 space-y-4">
              <div className="flex justify-center">
                <div className="relative">
                  <Star className="w-8 h-8 text-primary animate-pulse" />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">No active habits in this category</p>
                <button className="text-primary hover:text-primary/80 transition-colors mt-2">
                  Add your first habit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 theme-button py-3 rounded-lg flex items-center justify-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </button>
          <button
            onClick={handleDismiss}
            disabled={isSaving}
            className="flex-1 bg-muted hover:bg-muted/70 text-muted-foreground py-3 rounded-lg transition-colors"
          >
            Save for Later
          </button>
        </div>
      </div>

      {/* Exit Dialog */}
      {showExitDialog && (
        <HabitExitDialog
          onClose={() => setShowExitDialog(false)}
          onContinue={() => setShowExitDialog(false)}
          onDismiss={() => navigate('/home')}
        />
      )}
    </div>
  );
}